import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../database/firebase';

const NORMALIZED_PRIORITY = [
  'SuperAdmin',
  'Admin',
  'Moderator',
  'Coach',
  'Alumni',
  'Client',
  'PlatformEngineer',
];

export function normalizeRoleName(rawRole) {
  if (!rawRole) return 'Client';
  const val = String(rawRole).toLowerCase().trim();
  if (
    val === 'superadmin' ||
    val === 'superadmins' ||
    val === 'super admin' ||
    val === 's_admin' ||
    val === 's admin'
  )
    return 'SuperAdmin';
  if (val === 'admin' || val === 'admins') return 'Admin';
  if (
    val === 'platformengineer' ||
    val === 'platformengineers' ||
    val === 'platform engineer' ||
    val === 'developer' ||
    val === 'developers'
  )
    return 'PlatformEngineer';
  if (
    val === 'moderator' ||
    val === 'moderators' ||
    val === 'business advisor' ||
    val === 'business advisors' ||
    val === 'businessadvisor' ||
    val === 'businessadvisors'
  )
    return 'Moderator';
  if (val === 'coach' || val === 'coaches' || val === 'businesscoach') return 'Coach';

  if (val === 'alumni' || val === 'alumnis') return 'Alumni';
  return 'Client';
}

export async function resolveUserRolesByEmailOrUid({ email, uid }) {
  const roles = new Set();
  let matchedInClients = false;

  if (!email && !uid)
    return {
      roles: ['Client'],
      highestRole: 'Client',
      matchedInClients: false,
      documentBankStaffFullAccess: false,
    };

  const exactEmail = email ? String(email).trim().toLowerCase() : null;

  let documentBankStaffFullAccess = false;
  if (uid) {
    const staffSnaps = await Promise.all([
      getDoc(doc(firestore, 's_admin', uid)),
      getDoc(doc(firestore, 'admin', uid)),
      getDoc(doc(firestore, 'moderator', uid)),
      getDoc(doc(firestore, 'coaches', uid)),
      getDoc(doc(firestore, 'developer', uid)),
    ]);
    documentBankStaffFullAccess = staffSnaps.some(s => s.exists());
  }

  const lookups = [];

  if (exactEmail) {
    lookups.push(
      getDocs(query(collection(firestore, 's_admin'), where('email', '==', exactEmail))).then(s =>
        s.size > 0 ? 'SuperAdmin' : null
      )
    );
    lookups.push(
      getDocs(query(collection(firestore, 'admin'), where('email', '==', exactEmail))).then(s =>
        s.size > 0 ? 'Admin' : null
      )
    );
    lookups.push(
      getDocs(query(collection(firestore, 'moderator'), where('email', '==', exactEmail))).then(
        s => (s.size > 0 ? 'Moderator' : null)
      )
    );
    lookups.push(
      getDocs(query(collection(firestore, 'coaches'), where('email', '==', exactEmail))).then(s =>
        s.size > 0 ? 'Coach' : null
      )
    );
    lookups.push(
      getDocs(query(collection(firestore, 'businessAdvisors'), where('email', '==', exactEmail)))
        .then(s => (s.size > 0 ? 'Moderator' : null))
        .catch(() => null)
    );
    lookups.push(
      getDocs(query(collection(firestore, 'advisors'), where('email', '==', exactEmail)))
        .then(s => (s.size > 0 ? 'Moderator' : null))
        .catch(() => null)
    );
    lookups.push(
      getDocs(query(collection(firestore, 'alumni'), where('email', '==', exactEmail))).then(s =>
        s.size > 0 ? 'Alumni' : null
      )
    );
    lookups.push(
      getDocs(query(collection(firestore, 'clients'), where('email', '==', exactEmail))).then(s => {
        matchedInClients = s.size > 0 || matchedInClients;
        return s.size > 0 ? 'Client' : null;
      })
    );
    lookups.push(
      getDocs(query(collection(firestore, 'developer'), where('email', '==', exactEmail))).then(
        s => (s.size > 0 ? 'PlatformEngineer' : null)
      )
    );
  }

  if (uid) {
    lookups.push(
      getDocs(query(collection(firestore, 'coaches'), where('uid', '==', uid))).then(s =>
        s.size > 0 ? 'Coach' : null
      )
    );
  }

  const results = await Promise.all(lookups);
  results.filter(Boolean).forEach(r => roles.add(normalizeRoleName(r)));

  if (roles.size === 0) roles.add('Client');

  const ordered = Array.from(roles).sort(
    (a, b) => NORMALIZED_PRIORITY.indexOf(a) - NORMALIZED_PRIORITY.indexOf(b)
  );
  const highestRole = ordered[0];

  return { roles: ordered, highestRole, matchedInClients, documentBankStaffFullAccess };
}

export function isPrivileged(roles) {
  const set = new Set(roles || []);
  return (
    set.has('SuperAdmin') ||
    set.has('Admin') ||
    set.has('Moderator') ||
    set.has('Coach') ||
    set.has('PlatformEngineer')
  );
}
